import { Link } from 'react-router-dom';
import { Check, Copy, Info, Settings } from 'lucide-react';
import type { PodcastFeedKind, PodcastLibrary } from '@almanac/contracts';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { EpisodeCard, FeaturedEpisodeCard } from '../components/podcast/EpisodeCard';
import { DownloadAnalytics, usePodcastAnalytics } from '../components/podcast/DownloadAnalytics';
import { usePodcastLibrary } from '../components/podcast/hooks';
import { useCopyToClipboard } from '../lib/copy';
import { PODCAST_APP_STEPS, latestByFeed } from '../lib/podcast';

/**
 * The Podcast tab: the latest company episode and personal brief up top, a
 * compact row of feed links, and every episode below, by feed. Narrator and
 * weekly-brief setup live on `/podcast/setup`.
 */
export function PodcastPage() {
  const { state, retry } = usePodcastLibrary();
  const analytics = usePodcastAnalytics();

  return (
    <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
            Podcast
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your private weekly brief and Langston Product &amp; Engineering, in one place.
          </p>
        </div>
        <Link to="/podcast/setup" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
          <Settings />
          Setup
        </Link>
      </div>

      {state.status === 'loading' ? (
        <div className="mt-6 grid gap-4 md:grid-cols-2" aria-hidden>
          <div className="h-64 animate-pulse rounded-xl bg-muted" />
          <div className="h-64 animate-pulse rounded-xl bg-muted" />
        </div>
      ) : null}
      {state.status === 'error' ? (
        <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
          <p className="text-danger">{state.message}</p>
          <Button variant="outline" size="sm" onClick={retry}>
            Try again
          </Button>
        </div>
      ) : null}
      {state.status === 'ready' ? (
        <PodcastLibraryView library={state.library} onRetry={retry} analytics={analytics} />
      ) : null}
    </main>
  );
}

function PodcastLibraryView({
  library,
  onRetry,
  analytics,
}: {
  library: PodcastLibrary;
  onRetry: () => void;
  analytics: ReturnType<typeof usePodcastAnalytics>;
}) {
  const latest = latestByFeed(library.episodes);
  const latestCompany = latest.find((episode) => episode.feed === 'company');
  const latestPersonal = latest.find((episode) => episode.feed === 'personal');
  const company = library.episodes.filter((episode) => episode.feed === 'company');
  const personal = library.episodes.filter((episode) => episode.feed === 'personal');

  return (
    <>
      {library.unavailable.length > 0 ? (
        <div className="mt-6 flex flex-wrap items-center gap-3 rounded-md border-l-2 border-danger bg-danger/10 px-4 py-3 text-sm text-foreground">
          <p>Could not load the {library.unavailable.join(' and ')} feed just now.</p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}

      <section aria-labelledby="podcast-latest" className="mt-6">
        <h2 id="podcast-latest" className="sr-only">
          Latest episodes
        </h2>
        {library.episodes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No episodes yet.</p>
        ) : (
          <div className="grid items-start gap-4 md:grid-cols-2">
            {latestCompany ? <FeaturedEpisodeCard episode={latestCompany} /> : null}
            {latestPersonal ? (
              <FeaturedEpisodeCard episode={latestPersonal} />
            ) : (
              <NoBriefCard hasFeed={library.personal_feed_url !== null} />
            )}
          </div>
        )}
      </section>

      <SubscribeRow library={library} />

      <DownloadAnalytics result={analytics} episodes={library.episodes} />

      <section aria-labelledby="podcast-archive" className="mt-8">
        <h2 id="podcast-archive" className="font-display text-lg font-semibold text-foreground">
          All episodes
        </h2>
        <Tabs defaultValue="company" className="mt-3">
          <TabsList>
            <TabsTrigger value="company">Company ({company.length})</TabsTrigger>
            <TabsTrigger value="personal">My briefs ({personal.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="company">
            <EpisodeList episodes={company} empty="No company episodes yet." />
          </TabsContent>
          <TabsContent value="personal">
            <EpisodeList
              episodes={personal}
              empty="No briefs yet. Set one up from Setup, top right."
            />
          </TabsContent>
        </Tabs>
      </section>
    </>
  );
}

function EpisodeList({ episodes, empty }: { episodes: PodcastLibrary['episodes']; empty: string }) {
  if (episodes.length === 0) return <p className="mt-2 text-sm text-muted-foreground">{empty}</p>;
  return (
    <ol className="mt-1 space-y-3">
      {episodes.map((episode) => (
        <li key={`${episode.feed}:${episode.guid}`}>
          <EpisodeCard episode={episode} />
        </li>
      ))}
    </ol>
  );
}

/** Where the personal brief would be, before the caller has one. */
function NoBriefCard({ hasFeed }: { hasFeed: boolean }) {
  return (
    <Card className="items-start gap-2 px-4 sm:px-5">
      <h3 className="font-display text-lg font-semibold text-foreground">Your weekly brief</h3>
      <p className="text-sm text-muted-foreground">
        {hasFeed
          ? 'Your first brief is on its way. It lands here on Monday morning.'
          : 'A private three-minute recap of your week, every Monday morning.'}
      </p>
      {hasFeed ? null : (
        <Link to="/podcast/setup" className={buttonVariants({ size: 'sm' })}>
          Set it up
        </Link>
      )}
    </Card>
  );
}

/** Copy-link buttons for each feed, with podcast-app steps in a tooltip. */
function SubscribeRow({ library }: { library: PodcastLibrary }) {
  const { copiedKey, copyError, copy } = useCopyToClipboard();
  const feeds: { kind: PodcastFeedKind; label: string; url: string | null }[] = [
    { kind: 'company', label: 'Company feed', url: library.company_feed_url },
    { kind: 'personal', label: 'Your brief feed', url: library.personal_feed_url },
  ];

  return (
    <section
      aria-label="Subscribe"
      className="mt-6 flex flex-wrap items-center gap-2 rounded-xl bg-muted/50 px-4 py-3 text-sm"
    >
      <span className="mr-1 font-medium text-foreground">Listen in your podcast app</span>
      {feeds.map(({ kind, label, url }) =>
        url ? (
          <Button
            key={kind}
            variant="outline"
            size="sm"
            onClick={() => copy(kind, url)}
            title={url}
          >
            {copiedKey === kind ? <Check /> : <Copy />}
            {copiedKey === kind ? 'Copied' : `Copy ${label.toLowerCase()} link`}
          </Button>
        ) : null,
      )}
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="How to add a feed to a podcast app"
            />
          }
        >
          <Info />
        </TooltipTrigger>
        <TooltipContent side="bottom" className="block max-w-sm space-y-1 text-left">
          {PODCAST_APP_STEPS.map(({ app, steps }) => (
            <p key={app}>
              <span className="font-semibold">{app}:</span> {steps}
            </p>
          ))}
          <p>Spotify and YouTube Music can&apos;t follow private feeds.</p>
        </TooltipContent>
      </Tooltip>
      {copyError ? <p className="w-full text-xs text-danger">{copyError}</p> : null}
    </section>
  );
}
